// hooks/useServiceOrderFilters.ts
import { useSmartQuery } from "@shared/api/queries/hooks";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { useMemo, useState } from "react";
import { getActivityLog, getElectricUnit, getFlowCapacityUnit } from "./api";

export interface FilterConfig {
  searchTerm: string;
  serviceAssociation: string;
  status: string;
  priority?: string;
  area?: string;
  slaFilter?: string;
  soNameFilter?: string;
  performance?: string;
  selectedAssociations?: string[];
  selectedSONames?: string[];
}

export interface UseServiceOrderFiltersProps<T> {
  data: T[];
  filterFn: (item: T, filters: FilterConfig) => boolean;
  initialFilters?: Partial<FilterConfig>;
}

export function useServiceOrderFilters<T>({
  data,
  filterFn,
  initialFilters = {},
}: UseServiceOrderFiltersProps<T>) {
  const [filters, setFilters] = useState<FilterConfig>({
    searchTerm: "",
    serviceAssociation: "all",
    status: "all",
    priority: "all",
    area: "all",
    slaFilter: "all",
    soNameFilter: "",
    performance: "all",
    selectedAssociations: [],
    selectedSONames: [],
    ...initialFilters,
  });

  const filteredData = useMemo(() => {
    return data.filter((item) => filterFn(item, filters));
  }, [data, filters, filterFn]);

  const updateFilter = (key: keyof FilterConfig, value: string | string[]) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      searchTerm: "",
      serviceAssociation: "all",
      status: "all",
      priority: "all",
      area: "all",
      slaFilter: "all",
      soNameFilter: "",
      performance: "all",
      selectedAssociations: [],
      selectedSONames: [],
      ...initialFilters,
    });
  };

  return {
    filters,
    filteredData,
    updateFilter,
    clearFilters,
    setFilters,
  };
}

// hooks/useServiceOrderSelection.ts

export function useServiceOrderSelection() {
  const [selectedOrders, setSelectedOrders] = useState<string[]>([]);

  const handleSelectAll = (orderIds: string[], checked: boolean) => {
    if (checked) {
      setSelectedOrders(orderIds);
    } else {
      setSelectedOrders([]);
    }
  };

  const handleSelectOrder = (orderId: string, checked: boolean) => {
    if (checked) {
      setSelectedOrders((prev) => [...prev, orderId]);
    } else {
      setSelectedOrders((prev) => prev.filter((id) => id !== orderId));
    }
  };

  const clearSelection = () => setSelectedOrders([]);

  return {
    selectedOrders,
    handleSelectAll,
    handleSelectOrder,
    clearSelection,
  };
}

// hooks/useServiceOrderKPIs.ts

export interface KPIConfig<T> {
  data: T[];
  calculations: {
    [key: string]: (data: T[]) => number | string;
  };
}

export function useServiceOrderKPIs<T>({ data, calculations }: KPIConfig<T>) {
  const kpis = useMemo(() => {
    return Object.entries(calculations).reduce((acc, [key, fn]) => {
      acc[key] = fn(data);
      return acc;
    }, {} as Record<string, number | string>);
  }, [data, calculations]);

  return kpis;
}

export const useElectricUnit = () => {
  return useSmartQuery(
    QueryKeyFactory.module.wx.shared.electricUnitChoices(),
    () => getElectricUnit()
  );
};

export const useFlowCapacityUnit = () => {
  return useSmartQuery(
    QueryKeyFactory.module.wx.shared.flowCapacityUnitChoices(),
    () => getFlowCapacityUnit()
  );
};

export const useGetActivityLog = (id: string, type: string) => {
  return useSmartQuery(
    QueryKeyFactory.module.wx.activityLogList(id, type),
    () => getActivityLog(id, type)
  );
};